import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { toneBox } from '../../lib/tone';

/**
 * Empty, error and not-found states — the handoff's section 05.
 *
 * shadcn's `empty` component, re-toned. Two changes from the generated file:
 * the border is the solid hairline the handoff draws rather than shadcn's
 * dashed one, and a `tone` picks between the plain surface (nothing here yet,
 * page not found) and the amber wash the handoff uses for "couldn't load —
 * try again".
 *
 * Every empty state names the next action and every error state offers retry,
 * so `EmptyContent` is where the button goes rather than being optional
 * decoration.
 *
 * This is the one place that takes `toneBox` rather than `toneSurface`: the
 * block is big enough that tinting its title too would colour half a paragraph,
 * so the tone stops at the border and the fill.
 */
const emptyVariants = cva(
  'flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-card border p-6 text-center text-balance md:p-12',
  {
    variants: {
      tone: {
        /* Not `toneBox.neutral`: an empty list is not a quiet notice, it is the
         * page's own paper, so it stays on `surface` rather than `raised`. */
        neutral: 'border-hairline bg-surface',
        warning: toneBox.warning,
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

function Empty({
  className,
  tone,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyVariants>) {
  return (
    <div
      data-slot="empty"
      data-tone={tone ?? 'neutral'}
      className={cn(emptyVariants({ tone }), className)}
      {...props}
    />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-header"
      className={cn('flex max-w-sm flex-col items-center gap-2 text-center', className)}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: "flex size-10 shrink-0 items-center justify-center rounded-card bg-raised text-ink [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-media"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-title"
      className={cn('text-lg font-semibold tracking-tight', className)}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-sm/relaxed text-ink-muted [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-brand-text',
        className,
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance',
        className,
      )}
      {...props}
    />
  );
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia };
